/*
 * <variant-gallery> — per-SKU PDP photo gallery, powered by Swiper.
 *
 * Centered peek carousel (active slide centered, neighbours peeking) + pagination
 * dots. Reads a server-rendered JSON blob (#ccpdp-galleries-<section>) mapping each
 * variant id to an ordered image list ({ f: display url, full: full-res url, a: alt }) built from the
 * `custom.gallery` variant metafield. The selected variant's set is server-rendered
 * for LCP; on variant change (the product form's input[name="id"] is updated +
 * dispatches `change`), it rebuilds the WHOLE set for the selected SKU.
 *
 * Magnifier button → full-screen lightbox. Swipe/drag + clickable dots navigate.
 */
(function () {
  var ZOOM_SVG =
    '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>' +
    '<path d="m20 20-3.5-3.5M11 8v6M8 11h6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function slideHTML(it) {
    return (
      '<div class="swiper-slide">' +
      '<img class="ccpdp-gallery__img" src="' + esc(it.f) + '" data-full="' + esc(it.full || it.f) + '" alt="' + esc(it.a) + '" loading="lazy">' +
      '<button type="button" class="ccpdp-gallery__zoom" data-ccpdp-zoom aria-label="Zoom image">' + ZOOM_SVG + '</button>' +
      '</div>'
    );
  }

  class VariantGallery extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.section = this.dataset.section;
      this.activeId = this.dataset.activeVariant;
      this.mainEl = this.querySelector('[data-ccpdp-main]');
      this.paginationEl = this.querySelector('[data-ccpdp-pagination]');

      try {
        var raw = document.getElementById('ccpdp-galleries-' + this.section);
        this.galleries = JSON.parse((raw && raw.textContent) || '{}');
      } catch (e) {
        this.galleries = {};
      }

      // Wire load detection for the SSR slides immediately — independent of Swiper,
      // so a photo (and its fade-in) never gets stuck behind the loading placeholder
      // even if Swiper is slow/unavailable.
      this.markImages();
      this.whenSwiperReady(this.boot.bind(this));
    }

    // Reveal each slide's photo once it loads (fade-in); the pulsating placeholder
    // shows until then. Cached/complete images are revealed immediately.
    markImages() {
      if (!this.mainEl) return;
      this.mainEl.querySelectorAll('.swiper-slide').forEach(function (slide) {
        if (slide.classList.contains('is-loaded')) return;
        var img = slide.querySelector('.ccpdp-gallery__img');
        if (!img) { slide.classList.add('is-loaded'); return; }
        if (img.complete && img.naturalWidth > 0) { slide.classList.add('is-loaded'); return; }
        var done = function () { slide.classList.add('is-loaded'); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    }

    // swiper-bundle is a deferred sibling script (runs first); poll briefly as a guard.
    whenSwiperReady(cb) {
      if (window.Swiper) return cb();
      var tries = 0;
      var t = setInterval(function () {
        if (window.Swiper || tries++ > 100) { clearInterval(t); if (window.Swiper) cb(); }
      }, 50);
    }

    boot() {
      var self = this;
      this.swiper = new Swiper(this.mainEl, {
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween: 16,
        grabCursor: true,
        autoHeight: true,
        keyboard: { enabled: true },
        a11y: { enabled: true },
        pagination: { el: this.paginationEl, clickable: true },
        navigation: {
          prevEl: this.querySelector('[data-ccpdp-prev]'),
          nextEl: this.querySelector('[data-ccpdp-next]'),
        },
      });

      // magnifier → lightbox (delegated so rebuilt slides keep working)
      this.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ccpdp-zoom]');
        if (!btn) return;
        var slide = btn.closest('.swiper-slide');
        var img = slide && slide.querySelector('.ccpdp-gallery__img');
        if (img) self.openLightbox(img.dataset.full || img.currentSrc || img.src, img.alt);
      });

      this.applySingleFlag();
      this.watchVariant();
      this.bindAlign();
    }

    applySingleFlag() {
      var n = this.swiper ? this.swiper.slides.length : 0;
      this.toggleAttribute('data-single', n < 2);
    }

    // Top-align the gallery with the buy widget on desktop. Native blocks above the
    // widget (strain/title) reserve vertical space, so the widget card sits lower
    // than the gallery; measure the delta and nudge the gallery down to match.
    bindAlign() {
      var self = this;
      this.alignTop();
      requestAnimationFrame(function () { self.alignTop(); });
      setTimeout(function () { self.alignTop(); }, 300);
      setTimeout(function () { self.alignTop(); }, 1500);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { self.alignTop(); });
      var raf;
      window.addEventListener('resize', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () { self.alignTop(); });
      });
    }

    alignTop() {
      if (!window.matchMedia('(min-width: 990px)').matches) { this.style.marginTop = ''; return; }
      var widget = document.getElementById('ccr-buybox-' + this.section);
      if (!widget) return;
      this.style.marginTop = '0px';
      var d = Math.round(widget.getBoundingClientRect().top - this.getBoundingClientRect().top);
      if (d > 0) this.style.marginTop = d + 'px';
    }

    watchVariant() {
      var self = this;
      var form = document.getElementById(this.dataset.form);
      this.idInput = form ? form.querySelector('input[name="id"]') : null;
      var read = function () {
        var id = self.idInput ? String(self.idInput.value) : '';
        self.setVariant(id);
      };
      if (this.idInput) this.idInput.addEventListener('change', read);
      var vr = document.querySelector(
        'variant-radios[data-section="' + this.section + '"], variant-selects[data-section="' + this.section + '"]'
      );
      if (vr) vr.addEventListener('change', function () { setTimeout(read, 0); });
    }

    setVariant(id) {
      if (!id || id === this.activeId) return;
      var list = this.galleries[id];
      if (!list || !list.length) return; // no data for this SKU — keep current set
      this.activeId = id;
      this.swiper.removeAllSlides();
      this.swiper.appendSlide(list.map(slideHTML));
      this.swiper.update();
      this.swiper.slideTo(0, 0);
      this.swiper.updateAutoHeight(0);
      this.markImages();
      this.applySingleFlag();
    }

    openLightbox(src, alt) {
      if (!src) return;
      var box = document.createElement('div');
      box.className = 'ccpdp-lightbox';
      box.innerHTML =
        '<button type="button" class="ccpdp-lightbox__close" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        '<path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button>' +
        '<span class="ccpdp-lightbox__spinner" aria-hidden="true"></span>';
      // Build the full-res <img> with a load handler so we can fade it in (and drop
      // the spinner) only once the larger file has actually arrived.
      var img = document.createElement('img');
      img.className = 'ccpdp-lightbox__img';
      img.alt = alt || '';
      var reveal = function () { box.classList.add('is-loaded'); };
      img.addEventListener('load', reveal, { once: true });
      img.addEventListener('error', reveal, { once: true });
      img.src = src;
      if (img.complete && img.naturalWidth > 0) reveal(); // already cached
      box.appendChild(img);
      var close = function () { box.remove(); document.removeEventListener('keydown', onKey); };
      var onKey = function (e) { if (e.key === 'Escape') close(); };
      box.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
      document.body.appendChild(box);
    }
  }

  if (!customElements.get('variant-gallery')) {
    customElements.define('variant-gallery', VariantGallery);
  }
})();
